/**
 * Task 310 §2.3–§2.5 — the notifications poll's gate, scheduler, failure
 * classifier and snapshot reducer, lifted out of `NotificationsBell.tsx`.
 *
 * Nothing here imports React, and every time-, visibility- and
 * network-shaped dependency is injected. That is not decoration: the
 * repository's Vitest setup collects `*.test.ts` with no DOM environment
 * (`vitest.config.ts`), so a scheduler living inside a `useEffect` can
 * only ever be *described* by a test. `notificationsPolling.310.test.ts`
 * drives this module directly instead, with a fake clock, a fake
 * visibility source and deferred fetches.
 */

import { isApiError } from '../../api/client';
import type { MeContext, NotificationRow } from '../../api/types';
import { isNonOperationalContext, type MeStatus } from '../../context/MeContextProvider';
import { PERM, hasAnyPermission } from '../../permissions';

/**
 * Healthy cadence while the tab is visible. Byte-identical to the
 * pre-310 `POLL_INTERVAL_MS`: this task removes wasted polls, it does not
 * lengthen the healthy interval. A scheduling rule, not a freshness
 * guarantee (§2.4 rule 5).
 */
export const NOTIFICATIONS_POLL_INTERVAL_MS = 60_000;

/**
 * Floor between two attempt STARTS (§2.4 rule 4). A user alt-tabbing
 * repeatedly fires `visibilitychange` on every bounce; without a floor,
 * an already-due poller answers each one with a request.
 */
export const NOTIFICATIONS_MIN_ATTEMPT_SPACING_MS = 5_000;

/**
 * Ceiling on the consecutive-failure backoff (§2.4 rule 5). Applies ONLY
 * to consecutive failures — a success resets the cadence to
 * {@link NOTIFICATIONS_POLL_INTERVAL_MS}, so the backoff never delays
 * a poll that follows data we actually have.
 */
export const NOTIFICATIONS_MAX_ERROR_BACKOFF_MS = 300_000;

export interface NotificationsPollGate {
  /** May we spend a `list-notifications` request right now? */
  readonly shouldPoll: boolean;
  /** Whose snapshot is this, or null when there is no tenant identity. */
  readonly identityKey: string | null;
}

const GATE_CLOSED: NotificationsPollGate = { shouldPoll: false, identityKey: null };

/**
 * Task 310 §2.2/§2.3 — decide whether polling is worth a request, and
 * which identity any answer belongs to.
 *
 * THIS IS NOT AUTHORIZATION, and it cannot be. `me.permissions` may have
 * been replayed from the `kmi:me-context:` localStorage cache, which the
 * user owns. `list-notifications` plus RLS remain the only things that
 * decide which rows exist and who may read them. All this decides is
 * whether we are willing to spend a request.
 *
 * `identityKey` deliberately does NOT depend on `status`: during a manual
 * `refresh()` the provider holds the previous `me` while `status` is
 * `loading`, and the badge should keep the count it legitimately has
 * rather than blink to zero and back. Polling pauses; identity does not
 * change.
 */
export function notificationsPollGate(
  status: MeStatus,
  me: MeContext | null,
): NotificationsPollGate {
  if (me === null || me.system_admin) return GATE_CLOSED;
  // The single definition of the NON-OPERATIONAL shape — a real tenant
  // actor of a pending / suspended / terminated organization, answered
  // with `organization: null, roles: [], permissions: []`. The
  // `organization === null` test below is not a second opinion on it; it
  // is what narrows the type for the key.
  if (isNonOperationalContext(me)) return GATE_CLOSED;
  const organization = me.organization;
  if (organization === null) return GATE_CLOSED;
  const subjectUserId = me.supervised_session?.subject.user_id ?? '-';
  return {
    shouldPoll: status === 'ready' && hasAnyPermission(me.permissions, PERM.NOTIFICATION_READ),
    identityKey: `${me.actor.user_id}:${organization.id}:${subjectUserId}`,
  };
}

/**
 * What one completed attempt is allowed to tell the component.
 *
 * `empty` exists because `unauthenticated` and `not_found` are ANSWERS,
 * not failures: the pre-310 component already collapsed both to an empty
 * list (a 401 is handled centrally in `api/client.ts`, which signs out,
 * which unmounts the bell), and treating them as failures would compound
 * a routine sign-out into a backoff.
 */
export type NotificationsPollOutcome =
  | { readonly kind: 'rows'; readonly rows: ReadonlyArray<NotificationRow> }
  | { readonly kind: 'empty' }
  | { readonly kind: 'error'; readonly error: unknown };

function isAbortError(error: unknown): boolean {
  // Not `instanceof DOMException`: the same rejection is raised as a
  // plain `Error` named `AbortError` by some fetch implementations, and
  // `DOMException` is not guaranteed to exist off the browser.
  if (typeof error !== 'object' || error === null) return false;
  return (error as { readonly name?: unknown }).name === 'AbortError';
}

/**
 * Turn a rejected attempt into the outcome it deserves, or `null` when it
 * is an abort — an abort is not news, and the poller drops it.
 */
export function classifyNotificationsFailure(error: unknown): NotificationsPollOutcome | null {
  if (isAbortError(error)) return null;
  if (isApiError(error) && (error.code === 'unauthenticated' || error.code === 'not_found')) {
    return { kind: 'empty' };
  }
  return { kind: 'error', error };
}

export interface NotificationsSnapshot {
  /** The identity this list was fetched for. See §2.5. */
  readonly identityKey: string | null;
  readonly unread: ReadonlyArray<NotificationRow>;
  /** True when the last attempt failed for a reason worth showing. */
  readonly failed: boolean;
}

export function emptyNotificationsSnapshot(identityKey: string | null): NotificationsSnapshot {
  return { identityKey, unread: [], failed: false };
}

/**
 * Fold one outcome into the component's snapshot, stamped with the
 * identity of the poller that produced it.
 *
 * The identity comparison has exactly one job: a failure must not carry
 * ANOTHER identity's list forward. Keeping the last good list across a
 * transient error is the pre-310 behaviour and is preserved — but only
 * for the identity that earned it. Crossing that line is how a previous
 * user's unread count survives a re-login.
 */
export function reduceNotificationsSnapshot(
  current: NotificationsSnapshot,
  identityKey: string | null,
  outcome: NotificationsPollOutcome,
): NotificationsSnapshot {
  if (outcome.kind === 'rows') return { identityKey, unread: [...outcome.rows], failed: false };
  if (outcome.kind === 'empty') return emptyNotificationsSnapshot(identityKey);
  const carried = current.identityKey === identityKey ? current.unread : [];
  return { identityKey, unread: carried, failed: true };
}

/** Whatever the injected timer source hands back. `window.setTimeout` gives a number. */
export type NotificationsPollTimerHandle = number;

export interface NotificationsPollerOptions {
  readonly now: () => number;
  readonly setTimer: (fn: () => void, delayMs: number) => NotificationsPollTimerHandle;
  readonly clearTimer: (handle: NotificationsPollTimerHandle) => void;
  /** False whenever the document is hidden. Polling stops while it is. */
  readonly isVisible: () => boolean;
  readonly fetchUnread: (signal: AbortSignal) => Promise<ReadonlyArray<NotificationRow>>;
  readonly onOutcome: (outcome: NotificationsPollOutcome) => void;
  readonly intervalMs?: number;
  readonly minAttemptSpacingMs?: number;
  readonly maxErrorBackoffMs?: number;
}

export interface NotificationsPoller {
  /** Begin polling. Idempotent, and a no-op after {@link dispose}. */
  start(): void;
  /** Tell the poller the document's visibility may have changed. */
  notifyVisibilityChanged(): void;
  /**
   * Cancel the timer, abort the in-flight request, and guarantee that no
   * later settle reaches `onOutcome`. Idempotent.
   */
  dispose(): void;
}

/**
 * Task 310 §2.4 — the poll scheduler.
 *
 * It schedules against a DUE TIME rather than ticking. Every decision
 * funnels through one `evaluate()`, which is the only place that reads
 * visibility, the only place that starts a request and the only place
 * that arms a timer — so "visible but already fresh", "hidden", "already
 * in flight" and "due now" cannot drift apart into separate code paths
 * that each got it slightly wrong.
 */
export function createNotificationsPoller(
  options: NotificationsPollerOptions,
): NotificationsPoller {
  const intervalMs = options.intervalMs ?? NOTIFICATIONS_POLL_INTERVAL_MS;
  const spacingMs = options.minAttemptSpacingMs ?? NOTIFICATIONS_MIN_ATTEMPT_SPACING_MS;
  const maxBackoffMs = options.maxErrorBackoffMs ?? NOTIFICATIONS_MAX_ERROR_BACKOFF_MS;

  let started = false;
  let disposed = false;
  let timer: NotificationsPollTimerHandle | null = null;
  let inFlight: AbortController | null = null;
  let lastAttemptAt: number | null = null;
  let lastSuccessAt: number | null = null;
  let consecutiveFailures = 0;

  function cancelTimer(): void {
    if (timer === null) return;
    const handle = timer;
    timer = null;
    options.clearTimer(handle);
  }

  function abortInFlight(): void {
    if (inFlight === null) return;
    const controller = inFlight;
    // Cleared BEFORE the abort, so the settle handler's identity check
    // already fails even if the rejection were delivered synchronously.
    inFlight = null;
    controller.abort();
  }

  /**
   * When the next attempt becomes due.
   *
   * Failure backoff is keyed to the last attempt START, not to its
   * settle, so a request that hangs for the whole interval does not earn
   * a second full interval of silence on top.
   */
  function dueAt(): number {
    let due: number;
    if (consecutiveFailures > 0 && lastAttemptAt !== null) {
      due = lastAttemptAt + Math.min(intervalMs * 2 ** (consecutiveFailures - 1), maxBackoffMs);
    } else if (lastSuccessAt !== null) {
      due = lastSuccessAt + intervalMs;
    } else {
      // Nothing has ever answered: due immediately, subject to the floor.
      due = 0;
    }
    return lastAttemptAt === null ? due : Math.max(due, lastAttemptAt + spacingMs);
  }

  function evaluate(): void {
    if (disposed || !started) return;
    if (!options.isVisible()) {
      // Hidden: nothing pending, nothing in flight, nothing scheduled.
      cancelTimer();
      abortInFlight();
      return;
    }
    // Single flight. The settle re-evaluates, so nothing is lost.
    if (inFlight !== null) return;
    cancelTimer();
    const now = options.now();
    const due = dueAt();
    if (now >= due) {
      attempt(now);
      return;
    }
    timer = options.setTimer(() => {
      timer = null;
      evaluate();
    }, due - now);
  }

  function attempt(startedAt: number): void {
    lastAttemptAt = startedAt;
    const controller = new AbortController();
    inFlight = controller;
    void options.fetchUnread(controller.signal).then(
      (rows) => settle(controller, { kind: 'rows', rows }),
      (error: unknown) => settle(controller, classifyNotificationsFailure(error)),
    );
  }

  function settle(controller: AbortController, outcome: NotificationsPollOutcome | null): void {
    // The attempt this settle belongs to is no longer the live one: it was
    // aborted (hidden tab) or the poller was disposed. Deliver nothing.
    if (disposed || controller !== inFlight) return;
    inFlight = null;
    if (outcome === null) {
      // An abort that we did not order. Not news; the spacing floor keeps
      // the retry off the same millisecond.
      evaluate();
      return;
    }
    if (outcome.kind === 'error') {
      consecutiveFailures += 1;
    } else {
      consecutiveFailures = 0;
      lastSuccessAt = options.now();
    }
    options.onOutcome(outcome);
    evaluate();
  }

  return {
    start(): void {
      if (disposed || started) return;
      started = true;
      evaluate();
    },
    notifyVisibilityChanged(): void {
      evaluate();
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      cancelTimer();
      abortInFlight();
    },
  };
}
